'use client';

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { useOptionalTranslation } from '@/core/components/providers/TranslationProvider';

export interface CarouselPerView {
  desktop: number;
  tablet: number;
  mobile: number;
}

/** Settings keys every carousel-capable block shares (see property-editors/shared/CarouselFields). */
export interface CarouselSettings {
  perViewDesktop?: number;
  perViewTablet?: number;
  perViewMobile?: number;
  /** Seconds between automatic steps; 0 or unset = no autoplay. */
  autoplaySeconds?: number;
  loop?: boolean;
  arrows?: boolean;
  dots?: boolean;
}

/** `defaults` = what an unset toggle means for this block (products: no loop, like before). */
export function carouselPropsFromSettings(settings: CarouselSettings, fallback: CarouselPerView, defaults: { loop?: boolean } = {}) {
  const clamp = (value: unknown, fb: number, max: number) => {
    const n = Math.round(Number(value));
    return Number.isFinite(n) && n >= 1 ? Math.min(n, max) : fb;
  };
  return {
    perView: {
      desktop: clamp(settings.perViewDesktop, fallback.desktop, 8),
      tablet: clamp(settings.perViewTablet, fallback.tablet, 6),
      mobile: clamp(settings.perViewMobile, fallback.mobile, 4),
    },
    autoplayMs: Math.max(0, Number(settings.autoplaySeconds) || 0) * 1000,
    loop: settings.loop ?? defaults.loop ?? true,
    arrows: settings.arrows !== false,
    dots: settings.dots === true,
  };
}

interface CarouselProps {
  children: React.ReactNode[];
  perView: CarouselPerView;
  gap?: number;
  autoplayMs?: number;
  loop?: boolean;
  arrows?: boolean;
  dots?: boolean;
  /** The admin canvas's simulated viewport — real media queries don't follow it there. */
  viewport?: 'desktop' | 'tablet' | 'mobile';
  isAdmin?: boolean;
  ariaLabel?: string;
  className?: string;
  /** Phones show a sliver of the next item (items at 88 % width), so the row reads as swipeable. */
  peek?: boolean;
  /** 'inside': small arrows over the first/last item. 'edge': large arrows centred on the row's
   *  edges, from the tablet breakpoint up (phones swipe). */
  arrowPlacement?: 'inside' | 'edge';
  arrowColors?: { background?: string; color?: string };
}

/**
 * Horizontal carousel for builder blocks (logos, testimonials, products). Native scroll-snap does
 * the swiping, momentum and snapping (touch, trackpad, keyboard); JS only adds arrows, dots and
 * autoplay. Items are sized by CSS per breakpoint, so the server HTML already has the final layout
 * — no shift on hydration. Breakpoints match the builder's hide-on-device classes (640 / 1024 px).
 */
export function Carousel({
  children,
  perView,
  gap = 16,
  autoplayMs = 0,
  loop = true,
  arrows = true,
  dots = false,
  viewport,
  isAdmin = false,
  ariaLabel,
  className,
  peek = false,
  arrowPlacement = 'inside',
  arrowColors,
}: CarouselProps) {
  const t = useOptionalTranslation()?.t ?? ((_key: string, fallback?: string) => fallback ?? '');
  const items = React.Children.toArray(children);
  const scope = `carousel-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const trackRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  // Explicit pause by the visitor (WCAG 2.2.2) — hover/focus pauses are only temporary.
  const [userPaused, setUserPaused] = useState(false);
  const userPausedRef = useRef(false);
  userPausedRef.current = userPaused;
  const [positions, setPositions] = useState(0);
  const [active, setActive] = useState(0);

  // The scroll distance of one item, and how many positions (first visible item) the track has.
  const measure = useCallback(() => {
    const track = trackRef.current;
    const first = track?.firstElementChild as HTMLElement | null;
    if (!track || !first) return null;
    const step = first.offsetWidth + gap;
    const visible = Math.max(1, Math.round((track.clientWidth + gap) / step));
    return { track, step, count: Math.max(1, items.length - visible + 1) };
  }, [gap, items.length]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const update = () => {
      const m = measure();
      if (!m) return;
      setPositions(m.count);
      setActive(Math.min(m.count - 1, Math.round(track.scrollLeft / m.step)));
    };
    // No synchronous update() here: reading offsetWidth/clientWidth straight after React's commit
    // forces a layout of the freshly hydrated page (PageSpeed: ~200 ms "forced reflow" in this
    // chunk). ResizeObserver delivers its first callback right after the browser's own next
    // layout anyway, so the arrows/dots state arrives a frame later at no layout cost.
    track.addEventListener('scroll', update, { passive: true });
    let frame = 0;
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (observer) observer.observe(track);
    else frame = window.requestAnimationFrame(update);
    return () => {
      track.removeEventListener('scroll', update);
      observer?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [measure]);

  const goTo = useCallback((index: number) => {
    const m = measure();
    if (!m) return;
    let target = index;
    if (target >= m.count) target = loop ? 0 : m.count - 1;
    if (target < 0) target = loop ? m.count - 1 : 0;
    m.track.scrollTo({ left: target * m.step, behavior: 'smooth' });
  }, [loop, measure]);

  useEffect(() => {
    if (!autoplayMs || isAdmin || positions <= 1) return;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setInterval(() => {
      if (pausedRef.current || userPausedRef.current || document.hidden) return;
      const m = measure();
      if (!m) return;
      goTo(Math.round(m.track.scrollLeft / m.step) + 1);
    }, autoplayMs);
    return () => window.clearInterval(timer);
  }, [autoplayMs, isAdmin, positions, goTo, measure]);

  const pause = () => { pausedRef.current = true; };
  const resume = () => { pausedRef.current = false; };

  const peekScale = peek ? 0.88 : 1;
  const viewportVars = viewport
    ? ({ '--carousel-per-view': perView[viewport], '--carousel-item-scale': viewport === 'mobile' ? peekScale : 1 } as React.CSSProperties)
    : undefined;
  // The dots row's space is reserved by CSS wherever the items overflow (more items than fit per
  // view), so it doesn't pop in — and push the content below — once JS has measured the track.
  const overflowsAt = (count: number) => items.length > count;
  const dotsDisplay = (overflow: boolean) => (overflow ? 'flex' : 'none');
  const dotsCss = dots
    ? viewport
      ? `.${scope} > .carousel-dots { display: ${dotsDisplay(overflowsAt(perView[viewport]))}; }`
      : `.${scope} > .carousel-dots { display: ${dotsDisplay(overflowsAt(perView.mobile))}; }
@media (min-width: 640px) { .${scope} > .carousel-dots { display: ${dotsDisplay(overflowsAt(perView.tablet))}; } }
@media (min-width: 1024px) { .${scope} > .carousel-dots { display: ${dotsDisplay(overflowsAt(perView.desktop))}; } }`
    : '';
  const css = `
.${scope} { --carousel-per-view: ${perView.mobile}; --carousel-item-scale: ${peekScale}; }
@media (min-width: 640px) { .${scope} { --carousel-per-view: ${perView.tablet}; --carousel-item-scale: 1; } }
@media (min-width: 1024px) { .${scope} { --carousel-per-view: ${perView.desktop}; } }
.${scope} > .carousel-track::-webkit-scrollbar { display: none; }
.${scope} > .carousel-track > .carousel-item {
  flex: 0 0 calc((100% - (var(--carousel-per-view) - 1) * ${gap}px) / var(--carousel-per-view) * var(--carousel-item-scale));
}
${dotsCss}`;

  const canScroll = positions > 1;
  const edge = arrowPlacement === 'edge';
  const arrowClass = cn(
    'absolute top-1/2 -translate-y-1/2 z-10 rounded-full items-center justify-center transition-[opacity,transform] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
    edge
      ? cn('w-11 h-11 shadow-lg', isAdmin ? (viewport === 'mobile' ? 'hidden' : 'flex') : 'hidden sm:flex')
      : 'flex w-9 h-9 shadow-md border border-slate-200 hover:scale-105',
    !arrowColors?.background && 'bg-white/95',
    !arrowColors?.color && 'text-slate-800',
  );
  const arrowStyle = { backgroundColor: arrowColors?.background, color: arrowColors?.color };
  // Without looping, an arrow that has nowhere to go fades out instead of doing nothing.
  const atStart = !loop && active <= 0;
  const atEnd = !loop && active >= positions - 1;

  return (
    <section
      className={cn(scope, 'relative w-full', className)}
      style={viewportVars}
      aria-roledescription="carousel"
      aria-label={ariaLabel || t('carousel.label', 'Karuzela')}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
      onTouchStart={pause}
      onTouchEnd={resume}
    >
      <style>{css}</style>
      {!!autoplayMs && !isAdmin && positions > 1 && (
        <button
          type="button"
          onClick={() => setUserPaused((v) => !v)}
          aria-pressed={userPaused}
          aria-label={userPaused ? t('slider.play', 'Wznów przewijanie slajdów') : t('slider.pause', 'Zatrzymaj przewijanie slajdów')}
          className="absolute -top-1 right-0 z-10 flex h-7 w-7 -translate-y-full items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
        >
          {userPaused ? <Play className="h-3.5 w-3.5" aria-hidden /> : <Pause className="h-3.5 w-3.5" aria-hidden />}
        </button>
      )}
      <div
        ref={trackRef}
        className="carousel-track flex overflow-x-auto snap-x snap-mandatory overscroll-x-contain"
        // The padding/negative margin pair gives the items' hover lift and shadow room to show —
        // a horizontal scroller clips vertically too — without changing the block's height.
        style={{ gap: `${gap}px`, scrollbarWidth: 'none', padding: '8px 0 16px', margin: '-8px 0 -16px' }}
        tabIndex={0}
      >
        {items.map((item, index) => (
          <div
            key={index}
            className="carousel-item snap-start min-w-0"
            role="group"
            aria-roledescription="slide"
            aria-label={t('carousel.slide_of', '{n} z {total}').replace('{n}', String(index + 1)).replace('{total}', String(items.length))}
          >
            {item}
          </div>
        ))}
      </div>

      {arrows && canScroll && (
        <>
          <button
            type="button"
            className={cn(arrowClass, edge ? 'left-0 -translate-x-1/2' : 'left-1', atStart && 'opacity-0 pointer-events-none')}
            style={arrowStyle}
            tabIndex={atStart ? -1 : undefined}
            aria-label={t('carousel.prev', 'Poprzednie')}
            onClick={() => goTo(active - 1)}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            className={cn(arrowClass, edge ? 'right-0 translate-x-1/2' : 'right-1', atEnd && 'opacity-0 pointer-events-none')}
            style={arrowStyle}
            tabIndex={atEnd ? -1 : undefined}
            aria-label={t('carousel.next', 'Następne')}
            onClick={() => goTo(active + 1)}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {dots && (
        <div className="carousel-dots flex justify-center items-center gap-2 mt-4 h-2">
          {canScroll && Array.from({ length: positions }).map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={t('slider.goto_slide', 'Przejdź do slajdu {n}').replace('{n}', String(index + 1))}
              aria-current={index === active ? 'true' : undefined}
              onClick={() => goTo(index)}
              className={cn('h-2 rounded-full transition-all', index === active ? 'w-6 bg-slate-800' : 'w-2 bg-slate-300 hover:bg-slate-400')}
            />
          ))}
        </div>
      )}
    </section>
  );
}
